import { create } from 'zustand';
import { requiresAiSharingConsent } from '@/lib/appDistribution';
import { getStorage } from './storage';

export const AI_SHARING_CONSENT_VERSION = 1;
const keyFor = id => `ai-sharing-consent:v${AI_SHARING_CONSENT_VERSION}:${id}`;
const mobile = requiresAiSharingConsent;
let loadGeneration = 0;

// Account and device scoped. A new account, reinstall, changed disclosure or
// unreadable storage never inherits another user's permission.
export const useAiSharingConsent = create((set, get) => ({
  accountId: null, allowed: false, loading: false, error: null,
  load: async accountId => {
    const generation = ++loadGeneration;
    set({ accountId, allowed: false, loading: true, error: null });
    try {
      const raw = accountId ? await getStorage().getItem(keyFor(accountId)) : null;
      const receipt = raw ? JSON.parse(raw) : null;
      if (generation === loadGeneration) set({ loading: false, allowed: receipt?.version === AI_SHARING_CONSENT_VERSION && receipt?.accountId === accountId && receipt?.allowed === true });
    } catch (_) {
      if (generation === loadGeneration) set({ loading: false, allowed: false, error: 'Your saved choice could not be read. Please choose again.' });
    }
  },
  choose: async (accountId, allowed) => {
    if (!accountId || get().accountId !== accountId) throw new Error('Your account changed. Please try again.');
    // Invalidate any older storage read before persisting this explicit choice.
    ++loadGeneration;
    await getStorage().setItem(keyFor(accountId), JSON.stringify({ accountId, version: AI_SHARING_CONSENT_VERSION, allowed, decidedAt: new Date().toISOString() }));
    if (get().accountId === accountId) set({ allowed, loading: false, error: null });
  },
}));

export function hasAiSharingConsent(accountId) {
  if (!mobile()) return true;
  const state = useAiSharingConsent.getState();
  return !!accountId && state.accountId === accountId && !state.loading && state.allowed;
}

export function assertAiSharingConsent() {
  if (!mobile()) return;
  const accountId = require('./auth').useAuthStore.getState().user?.id;
  if (!hasAiSharingConsent(accountId)) {
    const error = new Error('Allow AI data sharing before starting an AI request. You can review your choice in Settings → General.');
    error.code = 'AI_SHARING_CONSENT_REQUIRED';
    throw error;
  }
}

// The screen gate prevents AI surfaces from mounting before consent. Guard
// requests as well, including queued socket sends and delayed/retried uploads.
export function requestNeedsAiSharing(url, method = 'GET') {
  const path = String(url).replace(/^https?:\/\/[^/]+/, '').split('?')[0];
  if (String(method).toUpperCase() === 'DELETE') return false;
  if (/\/(stop|end|cancel)$/.test(path)) return false;
  if (/^\/api\/voice\//.test(path)) return !/\/(status|voices|heartbeat)$/.test(path);
  if (/^\/json\/work\/(starter-prompts|project-starter-prompts)$/.test(path)) return true;
  if (['GET', 'HEAD', 'OPTIONS'].includes(String(method).toUpperCase())) return false;
  return /^\/json\/(tasks|work|task-schedules|inference-tasks|conversations|agents|files|tools|prompts|memory|maps|documents|spreadsheets|presentations|images|videos|music|account-onboarding)(\/|$)/.test(path);
}

export const AI_SOCKET_EVENTS = new Set(['send_prompt', 'regenerate_message', 'trigger_conversation_socket_inference', 'retrieve_conversation_title', 'continue_generation', 'fastAnswerNow']);
